import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSnackbar } from '@context/SnackbarContext'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { GroupFormValues } from '../Group.modal'

interface CreateOrUpdateRebateGroup {
  isLoading: boolean
  createOrUpdateRebateGroup: (values: GroupFormValues, groupId?: string) => Promise<void>
}

interface CreateOrUpdateRebateGroupParams {
  onCompleted?: () => void
}

export const useCreateOrUpdateRebateGroup = ({
  onCompleted,
}: CreateOrUpdateRebateGroupParams = {}): CreateOrUpdateRebateGroup => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)

  const createOrUpdateRebateGroup = async (values: GroupFormValues, groupId?: string) => {
    if (!account?.clientIntroducingBroker?.id) {
      return
    }

    setIsLoading(true)

    try {
      if (groupId) {
        await apiClient.updateRebateGroup(account.clientIntroducingBroker.id, groupId, values)
        addSnackbar.success({
          message: t('IB.RebatesPage.GroupsPage.Changes to rebate group were successfully saved'),
        })
      } else {
        await apiClient.createRebateGroup(account.clientIntroducingBroker.id, values)
        addSnackbar.success({
          message: t('IB.RebatesPage.GroupsPage.You’ve successfully created a rebate group'),
        })
      }

      onCompleted?.()
      setIsLoading(false)
    } catch (err: unknown) {
      if ((err as ResponseError).response?.response?.data.code) {
        const errorResponse = (err as ResponseError).response?.response?.data
        if (errorResponse?.code) {
          if (errorResponse?.code === 'introducing_broker_has_another_default_group') {
            addSnackbar.error({
              message: t('errors.Introducing Broker has another default group'),
            })
          }
        }
      }
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return { createOrUpdateRebateGroup, isLoading }
}
